import { useState } from 'react';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button/button.tsx';
import { ChatSuggestion, ChatSuggestions } from './chat-suggestions.tsx';

export interface ChatMultiSelectOption {
  id: string;
  title: string;
}

export interface ChatMultiSelectProps {
  /** Names the group for a screen reader, e.g. "Suggested questions". */
  label: string;
  options: ChatMultiSelectOption[];
  /** What the button that finishes the answer says, e.g. "Confirm". */
  confirmLabel: string;
  /**
   * Called once, with the titles picked, in the order they were offered rather
   * than the order they were clicked — how an answer reads should not depend on
   * which chip the user happened to hit first.
   */
  onConfirm: (titles: string[]) => void;
  /**
   * Whether there is still anything to answer here. A question the
   * conversation has moved past keeps its options on show, greyed, so what was
   * offered — and what was picked — can still be read.
   */
  disabled?: boolean;
  className?: string;
}

/**
 * A question that takes more than one answer.
 *
 * Clicking an option holds it rather than saying it, and nothing is said until
 * the user confirms — one message, however many options it names. An empty
 * answer is not an answer, so confirming is held until something is picked.
 */
export function ChatMultiSelect({
  label,
  options,
  confirmLabel,
  onConfirm,
  disabled = false,
  className,
}: ChatMultiSelectProps) {
  const [picked, setPicked] = useState<readonly string[]>([]);
  const chosen = options.filter((option) => picked.includes(option.id));

  function toggle(id: string) {
    setPicked((current) =>
      current.includes(id) ? current.filter((one) => one !== id) : [...current, id],
    );
  }

  return (
    <div data-slot='chat-multi-select' className={cn('flex flex-col gap-1.5', className)}>
      <ChatSuggestions label={label}>
        {options.map((option) => (
          <ChatSuggestion
            key={option.id}
            text={option.title}
            selected={picked.includes(option.id)}
            disabled={disabled}
            onClick={() => toggle(option.id)}
          />
        ))}
      </ChatSuggestions>

      <Button
        size='sm'
        disabled={disabled || chosen.length === 0}
        onClick={() => onConfirm(chosen.map((option) => option.title))}
        className='self-start'
      >
        {confirmLabel}
      </Button>
    </div>
  );
}
